import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { Facebook, Instagram, Linkedin, Building2, Twitter, Globe, CheckCircle, Clock, AlertCircle, Loader2, Timer, Film, Layers } from 'lucide-react';
import { getContentTypeBadgeColors, getContentTypeBadgeLabel } from '../task/ReelsStoryConstants';

interface TaskHoverPreviewProps {
  task: any;
  day: Date;
  children: React.ReactNode;
  columnIndex: number;
}

const renderPlatformIcon = (platform: string) => {
  const iconClass = "w-4 h-4 flex-shrink-0";
  switch (platform?.toLowerCase()) {
    case 'facebook':
      return <Facebook className={`${iconClass} text-blue-600`} />;
    case 'instagram':
    case 'instagram_business':
      return <Instagram className={`${iconClass} text-pink-600`} />;
    case 'linkedin':
      return <Linkedin className={`${iconClass} text-blue-700`} />;
    case 'google_business':
      return <Building2 className={`${iconClass} text-red-600`} />;
    case 'x':
      return <Twitter className={`${iconClass} text-black`} />;
    default:
      return <Globe className={`${iconClass} text-gray-600`} />;
  }
};

const getStatusBadge = (task: any) => {
  const publishedPost = task?.posts?.find((post: any) =>
    post.status === 'published' || post.platform_post_id || post.platform_post_url
  );

  if (publishedPost) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" />
        Published
      </span>
    );
  }

  const readyPost = task?.posts?.find((post: any) =>
    post.table === 'ready_posts' && post.is_default === true
  );

  if (readyPost) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
        <CheckCircle className="w-3 h-3" />
        Default Ready
      </span>
    );
  }

  // Ready but not yet promoted to default — awaiting auto-promotion
  // Skip superseded posts (old posts replaced by newer defaults)
  const awaitingReadyPost = task?.posts?.find((post: any) =>
    post.table === 'ready_posts' && !post.is_default && post.status !== 'superseded'
  );

  if (awaitingReadyPost) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Timer className="w-3 h-3 animate-pulse" />
        Ready (Awaiting)
      </span>
    );
  }

  const inProgressPost = task?.posts?.find((post: any) =>
    post.metadata?.is_in_progress === true || post.status === 'pending'
  );

  if (inProgressPost) {
    const stage = inProgressPost.metadata?.stage;
    if (stage === 'publishing') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
          <Loader2 className="w-3 h-3 animate-spin" />
          Publishing
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
        <Loader2 className="w-3 h-3 animate-spin" />
        In Progress
      </span>
    );
  }

  // Only show error if the LATEST post has error status
  // Old errors from previous executions should be ignored if a newer valid post exists
  const sortedPosts = (task?.posts || [])
    .filter((post: any) => post.table !== 'ready_posts')
    .slice()
    .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const latestPost = sortedPosts[0];
  if (latestPost?.status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <AlertCircle className="w-3 h-3" />
        Error
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <Clock className="w-3 h-3" />
      Scheduled
    </span>
  );
};

type PostContent = { content: string; imageUrl?: string; source?: { table: 'posts' | 'ready_posts'; id: string; version?: string } };

// The calendar list no longer carries post text (see PostScheduler POST_LIST_COLUMNS),
// so the hover fetches it once per post and keeps it here.
const contentCache = new Map<string, string | null>();

const getPostContent = (task: any): PostContent => {
  const readyPost = task?.posts?.find((post: any) =>
    post.table === 'ready_posts' && (post.is_default === true || post.status === 'ready')
  );

  if (readyPost) {
    return {
      content: readyPost.content || readyPost.generated_content || task.content,
      imageUrl: readyPost.image_url || readyPost.generated_image_url,
      source: readyPost.content === undefined ? { table: 'ready_posts', id: readyPost.id, version: readyPost.updated_at } : undefined
    };
  }

  const publishedPost = task?.posts?.find((post: any) =>
    post.status === 'published' || post.platform_post_id
  );

  if (publishedPost) {
    return {
      content: publishedPost.content || task.content,
      imageUrl: publishedPost.image_url || publishedPost.metadata?.image_url,
      source: publishedPost.content === undefined ? { table: 'posts', id: publishedPost.id, version: publishedPost.updated_at } : undefined
    };
  }

  return {
    content: task.content,
    imageUrl: task.image_url
  };
};

export const TaskHoverPreview: React.FC<TaskHoverPreviewProps> = ({
  task,
  day,
  children,
  columnIndex
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [position, setPosition] = useState<'left' | 'right'>('right');
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isRightEdge = columnIndex >= 5;

  const displayTime = task.display_date || parseISO(task.scheduled_for);
  const { content: baseContent, imageUrl, source } = getPostContent(task);
  // updated_at in the key: an edited post gets its new text
  const sourceKey = source ? `${source.table}:${source.id}:${source.version ?? ''}` : null;
  // Keyed by post, so a stale text never shows after the calendar reloads
  const [loaded, setLoaded] = useState<{ key: string; content: string | null } | null>(null);

  useEffect(() => {
    if (!isHovered || !source || !sourceKey) return;
    if (contentCache.has(sourceKey)) {
      setLoaded({ key: sourceKey, content: contentCache.get(sourceKey) ?? null });
      return;
    }
    let cancelled = false;
    supabase
      .from(source.table)
      .select('content')
      .eq('id', source.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('[TaskHoverPreview] content fetch failed:', error);
          if (!cancelled) setLoaded({ key: sourceKey, content: null });
          return;
        }
        contentCache.set(sourceKey, data?.content ?? null);
        if (!cancelled) setLoaded({ key: sourceKey, content: data?.content ?? null });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered, sourceKey]);

  const cachedContent = sourceKey
    ? (loaded?.key === sourceKey ? loaded.content : contentCache.get(sourceKey))
    : undefined;
  const isContentLoading = !!sourceKey && cachedContent === undefined;
  const content = sourceKey ? (cachedContent || task.content) : baseContent;

  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const tooltipWidth = 300;
      const windowWidth = window.innerWidth;

      let left: number;
      let showOnRight = !isRightEdge;

      if (showOnRight && rect.right + tooltipWidth + 10 > windowWidth) {
        showOnRight = false;
      }

      if (showOnRight) {
        left = rect.right + 8;
        setPosition('right');
      } else {
        left = rect.left - tooltipWidth - 8;
        setPosition('left');
      }

      let top = rect.top;
      const tooltipHeight = 300;
      if (top + tooltipHeight > window.innerHeight) {
        top = window.innerHeight - tooltipHeight - 20;
      }
      if (top < 10) {
        top = 10;
      }

      setTooltipPosition({ top, left: Math.max(10, left) });
    }
  };

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      updatePosition();
      setIsHovered(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const truncatedContent = content?.length > 200 ? content.substring(0, 200) + '...' : content;

  const tooltip = isHovered ? createPortal(
    <div
      className="fixed z-[9999] w-[300px] bg-white rounded-lg shadow-2xl border border-gray-200 p-4 pointer-events-none"
      style={{
        top: tooltipPosition.top,
        left: tooltipPosition.left,
      }}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 rounded-lg bg-gray-50">
          {renderPlatformIcon(task.platform)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">
              {task.connected_accounts?.name || task.platform}
            </span>
            {getStatusBadge(task)}
          </div>
          {task.content_type && task.content_type !== 'post' && (
            <div className="flex items-center gap-1 mt-1">
              {task.content_type === 'reel' ? <Film className="w-3 h-3 text-purple-500" /> : <Layers className="w-3 h-3 text-pink-500" />}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getContentTypeBadgeColors(task.content_type).bg} ${getContentTypeBadgeColors(task.content_type).text}`}>
                {getContentTypeBadgeLabel(task.content_type)}
                {task.content_type === 'story' && task.metadata?.story_parts_count && (
                  <span> · {task.metadata.story_parts_count} slides</span>
                )}
              </span>
            </div>
          )}
          <div className="text-xs text-gray-500 mt-0.5">
            {format(displayTime, 'MMM d, yyyy')} at {format(displayTime, 'HH:mm')}
          </div>
        </div>
      </div>

      {imageUrl && (
        <div className="mb-3 rounded-lg overflow-hidden bg-gray-100">
          <img
            src={imageUrl}
            alt="Post preview"
            className="w-full h-32 object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}

      <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
        {isContentLoading
          ? <span className="italic text-gray-400">Loading…</span>
          : truncatedContent || <span className="italic text-gray-400">No content</span>}
      </div>

      {task.cycle && task.cycle !== 'once' && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
            {task.cycle === 'daily' && 'Daily'}
            {task.cycle === 'weekly' && 'Weekly'}
            {task.cycle === 'monthly' && 'Monthly'}
            {task.cycle === 'custom' && `Custom: ${task.cycle_days?.join(', ')}`}
          </span>
        </div>
      )}

      <div
        className={`
          absolute top-4 w-2 h-2 bg-white border transform rotate-45
          ${position === 'right' ? '-left-1 border-l border-b border-gray-200' : '-right-1 border-r border-t border-gray-200'}
        `}
      />
    </div>,
    document.body
  ) : null;

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {tooltip}
    </div>
  );
};
